/**
 * Shared motion helpers for the security module's live-tracking Leaflet
 * maps (AdminTrackingPage, DriverTrackingPage, ParentSecurityPortalPage,
 * CollectorSharePage). Every one of those pages used to call
 * `marker.setLatLng(...)` directly on each poll/SSE update, which teleports
 * the icon instead of gliding it — this module is the fix, applied the
 * same way in all four rather than reinventing it per page.
 */

const EASE_OUT_CUBIC = (t) => 1 - Math.pow(1 - t, 3);

/** Smoothly moves a Leaflet marker to a new position over `duration` ms,
 * instead of snapping. Cancels any animation already in flight on the same
 * marker so rapid updates don't fight each other. */
export function animateMarkerTo(marker, [targetLat, targetLng], { duration = 1200 } = {}) {
  if (!marker) return;

  if (marker._moveAnimId) {
    cancelAnimationFrame(marker._moveAnimId);
    marker._moveAnimId = null;
  }

  const start = marker.getLatLng();
  const startLat = start.lat;
  const startLng = start.lng;
  if (startLat === targetLat && startLng === targetLng) return;

  const startTime = performance.now();

  const step = (now) => {
    const t = Math.min((now - startTime) / duration, 1);
    const eased = EASE_OUT_CUBIC(t);
    marker.setLatLng([
      startLat + (targetLat - startLat) * eased,
      startLng + (targetLng - startLng) * eased,
    ]);
    marker._moveAnimId = t < 1 ? requestAnimationFrame(step) : null;
  };
  marker._moveAnimId = requestAnimationFrame(step);
}

/** Stops any in-flight animateMarkerTo tween — call before removing a
 * marker from the map so the rAF loop doesn't keep running against it. */
export function stopMarkerAnimation(marker) {
  if (marker?._moveAnimId) {
    cancelAnimationFrame(marker._moveAnimId);
    marker._moveAnimId = null;
  }
}

/** Rotates the small heading-arrow badge inside a marker's icon (see the
 * `.map-heading-rotor` element built into the bus icon HTML) to point in
 * the direction of travel. `heading` is degrees clockwise from true north,
 * matching GeolocationCoordinates.heading — a no-op when unavailable
 * (stationary devices / most collector phones report null). */
export function setMarkerHeading(marker, heading) {
  if (!marker || heading === null || heading === undefined || Number.isNaN(heading)) return;
  const rotor = marker.getElement?.()?.querySelector('.map-heading-rotor');
  if (rotor) rotor.style.transform = `rotate(${heading}deg)`;
}

const DEFAULT_MAX_BREADCRUMB_POINTS = 30;

/** Appends a point to a capped breadcrumb trail array (oldest points drop
 * off), returning the new array — the caller feeds this straight into
 * `polyline.setLatLngs(...)`. */
export function pushBreadcrumbPoint(points, latlng, maxPoints = DEFAULT_MAX_BREADCRUMB_POINTS) {
  const next = [...points, latlng];
  if (next.length > maxPoints) next.shift();
  return next;
}
